import sanitizeHtml from 'sanitize-html';
import { getSetting, getSettingLabel } from '@koob/utils';
import ExportInaccessibleBadge from './ExportInaccessibleBadge';
import ExportDayMap from './ExportDayMap';
import ExportImagePlaceholder, {
  useImageFallback,
} from './ExportImagePlaceholder';

const MAP_WIDTH_PX = 272;
const MAP_HEIGHT_PX = 230;
const BANNER_HEIGHT_PX = 264;

function Stars({ count }) {
  if (!count) return null;
  return (
    <span className="text-base font-bold tracking-wider text-[var(--td-primary)]">
      {'★'.repeat(count)}
    </span>
  );
}

function EcoLeaves({ count }) {
  if (!count) return null;
  return (
    <span className="flex items-center gap-1">
      {[...Array(count)].map((_, i) => (
        <i key={i} className="fa-regular fa-leaf text-k-green text-xs" />
      ))}
    </span>
  );
}

function Divider() {
  return <span aria-hidden className="h-4 w-px bg-k-gray" />;
}

export default function ExportDayHotelCard({
  name,
  address,
  stars,
  sustainableLevel,
  roomName,
  mealPlan,
  description,
  image,
  isAccessible = true,
  nightIndex,
  totalNights,
  condensed,
  hotel,
  theme,
  settings,
}) {
  const daysSection = getSetting(settings, 'days');
  const accommodationSubsection = getSetting(
    daysSection?.sections,
    'accommodation',
  );
  const label = (slug) =>
    getSettingLabel(accommodationSubsection?.fields, slug);

  const [imageBroken, handleImageError] = useImageFallback();
  const showImage = !!image && !imageBroken;

  const nightWord = label('accom-night');
  const nightsWord = label('accom-nights');
  const badge = condensed
    ? `${nightWord} ${nightIndex} ${label('accom-night-of')} ${totalNights} · ${label('accom-same-accommodation')}`
    : `${accommodationSubsection?.displayName} · ${totalNights} ${
        totalNights > 1 ? nightsWord : nightWord
      }`;

  const nameRow = (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
      <div
        className="text-[22px] font-medium leading-tight"
        style={{ fontFamily: 'var(--td-font-display)' }}
      >
        {name}
      </div>
      {isAccessible === false && <ExportInaccessibleBadge />}
    </div>
  );

  const roomsRow = (roomName || mealPlan) && (
    <div
      className="mb-2 grid grid-cols-2 gap-3 border-y border-k-gray/40 py-2"
      data-prevent-cut
    >
      {roomName && (
        <div>
          <div className="text-2xs font-bold uppercase tracking-wider text-[var(--td-ink-muted)]">
            {label('accom-rooms-title')}
          </div>
          <div className="text-xs font-semibold leading-tight">{roomName}</div>
        </div>
      )}
      {/* An unknown meal plan is left out: no meal is not the same as unknown. */}
      {mealPlan && (
        <div>
          <div className="text-2xs font-bold uppercase tracking-wider text-[var(--td-ink-muted)]">
            {label('accom-meal-plan')}
          </div>
          <div className="text-xs font-semibold leading-tight">{mealPlan}</div>
        </div>
      )}
    </div>
  );

  if (condensed) {
    return (
      <div className="border border-k-gray/40 bg-white" data-prevent-cut>
        <div className="flex items-stretch gap-5 p-5">
          <div className="h-32 w-44 flex-shrink-0 overflow-hidden">
            {showImage ? (
              <img
                src={image}
                alt=""
                crossOrigin="anonymous"
                onError={handleImageError}
                className="h-full w-full object-cover"
              />
            ) : (
              <ExportImagePlaceholder tinted small className="h-full w-full" />
            )}
          </div>

          <div className="flex flex-1 flex-col justify-center min-w-0">
            <div className="text-2xs font-bold uppercase tracking-widest text-[var(--td-primary)]">
              {badge}
            </div>
            <div className="mt-1">{nameRow}</div>
            {address && (
              <div className="text-xs text-[var(--td-ink-muted)]">
                {address}
              </div>
            )}
            {roomsRow && <div className="mt-2">{roomsRow}</div>}
            <div className="mt-2 flex flex-wrap items-center gap-3">
              <Stars count={stars} />
              {sustainableLevel > 0 && (
                <>
                  <Divider />
                  <EcoLeaves count={sustainableLevel} />
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="border border-k-gray/40 bg-white">
      <div
        className="relative w-full overflow-hidden"
        style={{ height: `${BANNER_HEIGHT_PX}px` }}
        data-prevent-cut
      >
        {showImage ? (
          <img
            src={image}
            alt=""
            crossOrigin="anonymous"
            onError={handleImageError}
            className="h-full w-full object-cover"
          />
        ) : (
          <ExportImagePlaceholder tinted className="h-full w-full" />
        )}
        {/* Without a photo behind it, the overlay switches to the page's ink. */}
        <div
          className={`absolute inset-x-0 bottom-0 px-5 py-3 ${
            showImage
              ? 'bg-gradient-to-t from-black/60 to-transparent text-white'
              : 'text-[var(--td-ink)]'
          }`}
        >
          <div
            className={`text-2xs font-bold uppercase tracking-widest ${
              showImage ? '' : 'text-[var(--td-primary)]'
            }`}
          >
            {badge}
          </div>
          <div className="mt-0.5">{nameRow}</div>
          {address && (
            <div
              className={`text-xs ${
                showImage ? 'opacity-80' : 'text-[var(--td-ink-muted)]'
              }`}
            >
              {address}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-12 gap-5 p-5">
        <div className="col-span-5 flex flex-col">
          <div className="mb-2 text-2xs font-bold uppercase tracking-widest text-[var(--td-primary)]">
            {label('accom-hotel-location')}
          </div>
          <ExportDayMap
            hotel={hotel}
            theme={theme}
            width={MAP_WIDTH_PX}
            height={MAP_HEIGHT_PX}
            className="w-full"
            style={{ height: `${MAP_HEIGHT_PX}px` }}
          />
        </div>

        <div className="col-span-7 flex flex-col min-w-0">
          {(stars > 0 || sustainableLevel > 0) && (
            <div
              className="mb-2 flex flex-wrap items-center gap-3"
              data-prevent-cut
            >
              <Stars count={stars} />
              {sustainableLevel > 0 && (
                <>
                  <Divider />
                  <EcoLeaves count={sustainableLevel} />
                  <span className="text-2xs font-bold uppercase tracking-wider text-[var(--td-ink-muted)]">
                    {label('accom-sustainability')}
                  </span>
                </>
              )}
            </div>
          )}

          {roomsRow}

          {description && (
            <div
              className="text-xs leading-relaxed text-[var(--td-ink-soft)]"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(description) }}
            />
          )}
        </div>
      </div>
    </div>
  );
}
